import React from 'react';
import styled, {css} from 'styled-components';
import {useFragment, graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {Icon, tokensForThemeProvider} from '@forecast-it/design-system';
import {D} from '../ai-chat/design-tokens';
import {PromptRow_savedPrompt$key} from './__generated__/PromptRow_savedPrompt.graphql';

const {font} = tokensForThemeProvider.sky.sys;

const bodyFont = css`
	${font.body['1']}
`;

const PromptRow_savedPromptFragment = graphql`
	fragment PromptRow_savedPrompt on SavedPrompt {
		id
		name
		promptText
	}
`;

const Wrapper = styled.div<{selected?: boolean}>`
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 100%;
	position: relative;
	background: ${({selected}) => (selected ? D.promptRowSelectedBg : 'transparent')};

	&:hover {
		background: ${({selected}) => (selected ? D.promptRowSelectedBg : D.v2.surfaceHover)};
	}
`;

const Row = styled.button`
	${bodyFont}
	display: flex;
	align-items: center;
	flex: 1;
	min-width: 0;
	height: 44px;
	padding: 0 16px;
	border: none;
	background: transparent;
	cursor: pointer;
	text-align: left;
	color: ${D.v2.textPrimary};
`;

const RowText = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	display: block;
	width: 100%;
`;

const TrashButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	flex-shrink: 0;
	background: transparent;
	border: none;
	cursor: pointer;
	color: ${D.v2.textSecondary};
	padding: 0;
	opacity: 0;
	transition: color 100ms ease, opacity 100ms ease;

	&:hover {
		color: ${D.v2.statusRed};
	}

	${Wrapper}:hover & {
		opacity: 1;
	}
`;

type PromptRowProps = {
	savedPromptRef: PromptRow_savedPrompt$key;
	onSelect: (promptText: string) => void;
	onDelete?: (id: string) => void;
	selected?: boolean;
};

const PromptRow = ({savedPromptRef, onSelect, onDelete, selected}: PromptRowProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const prompt = useFragment(PromptRow_savedPromptFragment, savedPromptRef);

	return (
		<Wrapper selected={selected}>
			<Row type="button" onClick={() => onSelect(prompt.promptText ?? '')}>
				<RowText title={prompt.name ?? undefined}>{prompt.name}</RowText>
			</Row>
			{onDelete && (
				<TrashButton
					type="button"
					aria-label={formatMessage({id: 'nova.prompt.delete_label'})}
					onClick={e => {
						e.stopPropagation();
						onDelete(prompt.id);
					}}
				>
					<Icon icon="trash" size="m" />
				</TrashButton>
			)}
		</Wrapper>
	);
};

export default PromptRow;
